import React, { useState, useEffect, useCallback } from 'react';
import { siteDatabaseAPI } from '../../../services/api';
import AttendanceMap from '../../map/AttendanceMap';
import AttendanceMarkerPopup from '../../map/AttendanceMarkerPopup';
import BottomSheet from '../../common/BottomSheet';
import ResponsiveModal from '../../common/ResponsiveModal';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { format } from 'date-fns';

// Stat card component - optimized for mobile
const StatCard = ({ label, value, icon, color = 'gray', loading = false }) => {
  const colorClasses = {
    green: 'bg-green-50 text-green-700 border-green-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200'
  };

  return (
    <div className={`rounded-lg border p-3 sm:p-4 ${colorClasses[color]}`}>
      <div className="flex items-center gap-2 sm:gap-3">
        <div className="text-xl sm:text-2xl">{icon}</div>
        <div className="min-w-0 flex-1">
          <p className="text-xs sm:text-sm font-medium opacity-80 truncate">{label}</p>
          {loading ? (
            <div className="h-6 sm:h-8 w-10 sm:w-12 bg-current opacity-20 rounded animate-pulse" />
          ) : (
            <p className="text-xl sm:text-2xl font-bold">{value}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Missing user card component - optimized for mobile
const MissingUserCard = ({ user, type }) => {
  const isManager = type === 'site_manager';

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-2.5 sm:p-3 hover:shadow-sm active:bg-gray-50 transition-all">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 text-sm sm:text-base truncate">
            {user.nickname || user.fullName}
          </p>
          <p className="text-xs text-gray-500 truncate hidden sm:block">{user.email}</p>
        </div>
        {user.daysSinceLastCheckIn !== null && (
          <span className={`text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full flex-shrink-0 ${
            user.daysSinceLastCheckIn > 7
              ? 'bg-red-100 text-red-700'
              : user.daysSinceLastCheckIn > 3
              ? 'bg-orange-100 text-orange-700'
              : 'bg-gray-100 text-gray-600'
          }`}>
            {user.daysSinceLastCheckIn}d
          </span>
        )}
        {user.daysSinceLastCheckIn === null && (
          <span className="text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full bg-gray-100 text-gray-500 flex-shrink-0">
            Never
          </span>
        )}
      </div>

      <div className="mt-1.5 sm:mt-2 flex flex-wrap gap-1.5 sm:gap-2 text-xs text-gray-500">
        {isManager ? (
          <>
            <span className="inline-flex items-center gap-1">
              <svg className="w-3 h-3 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {user.supervisorsCount} <span className="hidden sm:inline">supervisors</span><span className="sm:hidden">SS</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <svg className="w-3 h-3 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              {user.assignedSitesCount} sites
            </span>
          </>
        ) : (
          <>
            {user.siteManagerName && (
              <span className="inline-flex items-center gap-1 truncate max-w-[120px] sm:max-w-none">
                <svg className="w-3 h-3 flex-shrink-0 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="truncate">{user.siteManagerName}</span>
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <svg className="w-3 h-3 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              {user.assignedSitesCount} sites
            </span>
          </>
        )}
      </div>

      {user.lastCheckInDate && (
        <p className="mt-1.5 sm:mt-2 text-xs text-gray-400">
          <span className="hidden sm:inline">Last check-in: </span>
          {format(new Date(user.lastCheckInDate), 'MMM d, h:mm a')}
        </p>
      )}
    </div>
  );
};

// Collapsed summary card - opens a ResponsiveModal when tapped
const MissingCheckInsList = ({ title, users, type, loading, onOpen }) => {
  const isEmpty = !loading && users.length === 0;
  const count = users.length;
  const disabled = loading || isEmpty;

  const badgeClass = loading
    ? 'bg-gray-100 text-gray-500'
    : count === 0
      ? 'bg-green-100 text-green-700'
      : count > 5
        ? 'bg-red-100 text-red-700'
        : 'bg-orange-100 text-orange-700';

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onOpen}
      disabled={disabled}
      className={`w-full bg-white rounded-lg border border-gray-200 px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between gap-3 text-left transition-colors ${
        disabled ? 'cursor-default' : 'hover:bg-gray-50 active:bg-gray-100'
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        {isEmpty && (
          <div className="w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
        <div className="min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{title}</h3>
          <p className="text-xs text-gray-500 truncate">
            {loading
              ? 'Loading...'
              : isEmpty
                ? 'All checked in today'
                : `${count} missing · tap to view`}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className={`text-xs sm:text-sm px-2 py-0.5 rounded-full ${badgeClass}`}>
          {loading ? '...' : count}
        </span>
        {!disabled && (
          <>
            <span className="sr-only">View all {title}</span>
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </>
        )}
      </div>
    </button>
  );
};

// Full personnel list rendered inside a ResponsiveModal (BottomSheet on mobile, Modal on desktop)
const MissingCheckInsModal = ({ isOpen, onClose, title, users, type }) => {
  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} title={title} size="md" maxHeight="85vh">
      <div className="p-3 sm:p-4">
        {users.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm text-gray-600">All personnel have checked in today!</p>
          </div>
        ) : (
          <>
            <p className="text-xs sm:text-sm text-gray-500 mb-3">
              {users.length} personnel missing check-in today
            </p>
            <div className="space-y-2 sm:space-y-3">
              {users.map(user => (
                <MissingUserCard key={user.id} user={user} type={type} />
              ))}
            </div>
          </>
        )}
      </div>
    </ResponsiveModal>
  );
};

export default function SiteAttendanceMapTab() {
  const [activeCheckIns, setActiveCheckIns] = useState([]);
  const [missingCheckIns, setMissingCheckIns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Mobile bottom sheet state
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [selectedCheckIn, setSelectedCheckIn] = useState(null);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

  // Missing check-ins modal state: null | 'site_supervisor' | 'site_manager'
  const [openList, setOpenList] = useState(null);

  // Handler for marker clicks on mobile - opens bottom sheet
  const handleMarkerClick = useCallback((checkIn) => {
    setSelectedCheckIn(checkIn);
    setIsBottomSheetOpen(true);
  }, []);

  // Handler for closing bottom sheet
  const handleBottomSheetClose = useCallback(() => {
    setIsBottomSheetOpen(false);
    // Delay clearing selection for smooth close animation
    setTimeout(() => setSelectedCheckIn(null), 300);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setError(null);

      const [activeResponse, missingResponse] = await Promise.all([
        siteDatabaseAPI.getActiveCheckIns(),
        siteDatabaseAPI.getMissingCheckIns()
      ]);

      setActiveCheckIns(activeResponse.data.data || []);
      setMissingCheckIns(missingResponse.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching attendance map data:', err);
      setError(err.response?.data?.message || 'Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 60000);

    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = () => {
    setLoading(true);
    fetchData();
  };

  if (error && !activeCheckIns.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <p className="text-gray-900 font-medium mb-2">Failed to Load Data</p>
        <p className="text-gray-500 text-sm mb-4">{error}</p>
        <button
          onClick={handleRefresh}
          className="px-4 py-2 bg-tenq-gold text-white rounded-lg hover:bg-tenq-gold/90 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header with refresh - stacks on mobile */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
        <div className="flex-1 min-w-0">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">Live Attendance Map</h2>
          {lastUpdated && (
            <p className="text-xs text-gray-500 truncate">
              <span className="hidden sm:inline">Last updated: </span>
              <span className="sm:hidden">Updated </span>
              {format(lastUpdated, 'h:mm a')}
              <span className="hidden sm:inline"> · Auto-refreshes every minute</span>
            </p>
          )}
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 px-3 py-2 sm:py-1.5 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 transition-colors w-full sm:w-auto"
        >
          <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Summary Stats - horizontal scroll on very small screens */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <StatCard
          label="Active"
          value={activeCheckIns.length}
          icon="📍"
          color="green"
          loading={loading}
        />
        <StatCard
          label="Missing SS"
          value={missingCheckIns?.summary?.missingSiteSupervisors ?? 0}
          icon="👷"
          color={missingCheckIns?.summary?.missingSiteSupervisors > 0 ? 'orange' : 'green'}
          loading={loading}
        />
        <StatCard
          label="Missing SM"
          value={missingCheckIns?.summary?.missingSiteManagers ?? 0}
          icon="👔"
          color={missingCheckIns?.summary?.missingSiteManagers > 0 ? 'red' : 'green'}
          loading={loading}
        />
      </div>

      {/* Map Section - responsive height. relative z-0 creates a stacking context so Leaflet's internal z-indexes stay below modals. */}
      <div className="relative z-0 bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
        <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3">
          <span className="hidden sm:inline">Personnel Currently on Sites</span>
          <span className="sm:hidden">On Site Now</span>
          {activeCheckIns.length > 0 && (
            <span className="ml-1 sm:ml-2 text-xs text-gray-500">
              ({activeCheckIns.length})
            </span>
          )}
        </h3>
        <AttendanceMap
          checkIns={activeCheckIns}
          isMobile={isMobile}
          onMarkerClick={handleMarkerClick}
        />
      </div>

      {/* Missing Check-ins Section */}
      <div>
        <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3">
          <span className="hidden sm:inline">Personnel Who Haven't Checked In Today</span>
          <span className="sm:hidden">Missing Check-ins</span>
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
          <MissingCheckInsList
            title="Site Supervisors"
            users={missingCheckIns?.data?.siteSupervisors || []}
            type="site_supervisor"
            loading={loading}
            onOpen={() => setOpenList('site_supervisor')}
          />
          <MissingCheckInsList
            title="Site Managers"
            users={missingCheckIns?.data?.siteManagers || []}
            type="site_manager"
            loading={loading}
            onOpen={() => setOpenList('site_manager')}
          />
        </div>
      </div>

      {/* Full missing check-ins list - bottom sheet on mobile, modal on desktop */}
      <MissingCheckInsModal
        isOpen={openList !== null}
        onClose={() => setOpenList(null)}
        title={openList === 'site_manager' ? 'Site Managers' : 'Site Supervisors'}
        users={
          openList === 'site_manager'
            ? missingCheckIns?.data?.siteManagers || []
            : missingCheckIns?.data?.siteSupervisors || []
        }
        type={openList || 'site_supervisor'}
      />

      {/* Summary footer */}
      {!loading && missingCheckIns && (
        <div className="text-center text-xs text-gray-400 px-2">
          <span className="hidden sm:inline">
            Total: {missingCheckIns.summary?.totalSiteSupervisors || 0} Site Supervisors, {missingCheckIns.summary?.totalSiteManagers || 0} Site Managers
          </span>
          <span className="sm:hidden">
            {missingCheckIns.summary?.totalSiteSupervisors || 0} SS · {missingCheckIns.summary?.totalSiteManagers || 0} SM total
          </span>
        </div>
      )}

      {/* Mobile Bottom Sheet for marker details */}
      {isMobile && (
        <BottomSheet
          isOpen={isBottomSheetOpen}
          onClose={handleBottomSheetClose}
          title={selectedCheckIn?.userNickname || selectedCheckIn?.userName || 'Check-in Details'}
          maxHeight="70vh"
        >
          {selectedCheckIn && (
            <div className="p-4">
              <AttendanceMarkerPopup
                checkIn={selectedCheckIn}
                variant="bottomsheet"
                onClose={handleBottomSheetClose}
              />
            </div>
          )}
        </BottomSheet>
      )}
    </div>
  );
}
